import {
    BaseInput,
    CurrencyType,
    DatePicker,
    ProviderCreateCollectionPaymentPayload,
    ProviderPatientStatement,
    IToastContextProps,
} from '@enter/core';
import { mdiCashMultiple } from '@mdi/js';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { IClaimStore, IDialogContext } from '../statement-details.types';
import { StatementApplyToClaims } from './statement-apply-to-claims';
import {
    allocateAmountAcrossClaims,
    getPayableClaims,
    resolveTargetClaims,
    sortClaimsByOldestDos,
} from './statement-apply-to-claims.utils';
import { parseLedgerAmount } from '../utils/statement-format.utils';
import * as S from './statement-ledger-dialog.styles';

type RecordPaymentDialogProps = {
    patientStatement?: ProviderPatientStatement;
    onClose: () => void;
    onSuccess: () => void;
    claimStore: IClaimStore;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
};

export const RecordPaymentDialog: React.FunctionComponent<RecordPaymentDialogProps> = (props) => {
    const { claimStore, dialogContext, toastContext, patientStatement, onClose, onSuccess } = props;

    const [paymentDate, setPaymentDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [amount, setAmount] = useState<string>('');
    const [memo, setMemo] = useState<string>('');
    const [selectedClaimIds, setSelectedClaimIds] = useState<Record<string, boolean>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const payableClaims = useMemo(() => getPayableClaims(patientStatement), [patientStatement]);

    const targetClaims = useMemo(
        () => resolveTargetClaims(payableClaims, 'manual', selectedClaimIds),
        [payableClaims, selectedClaimIds],
    );

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        const parsedAmount = parseLedgerAmount(amount);

        if (!paymentDate) {
            newErrors.paymentDate = 'Payment date is required';
        }

        if (!amount || parsedAmount <= 0) {
            newErrors.amount = 'Amount is required and must be greater than 0';
        }

        if (!targetClaims.length) {
            newErrors.claims = payableClaims.length
                ? 'Select at least one claim'
                : 'This statement has no claims to apply a payment to.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (): Promise<void> => {
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const parsedAmount = parseLedgerAmount(amount);
            const orderedClaims = [...targetClaims].sort(sortClaimsByOldestDos);
            const allocations = allocateAmountAcrossClaims(orderedClaims, parsedAmount);

            if (!allocations.length) {
                throw new Error('Unable to allocate payment to selected claims');
            }

            for (const allocation of allocations) {
                const payload: ProviderCreateCollectionPaymentPayload = {
                    collectedAmount: {
                        amount: allocation.amount.toFixed(2),
                        currency: CurrencyType.USD,
                    },
                    claimId: allocation.claimId,
                    paymentDate,
                    memo: memo.trim() || undefined,
                };
                await claimStore.createCollectionPayment(payload);
            }

            toastContext.notify?.(
                allocations.length > 1
                    ? `Payment was recorded across ${allocations.length} claims.`
                    : 'Payment was recorded successfully.',
                'right',
            );
            onSuccess();
        } catch (err) {
            dialogContext.showError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDateChange = (date: Date | undefined): void => {
        if (date) {
            setPaymentDate(format(date, 'yyyy-MM-dd'));
        } else {
            setPaymentDate('');
        }
    };

    const selectClaim = (claimId: string): void => {
        setSelectedClaimIds({ [claimId]: true });
    };

    return (
        <S.DialogBody>
            <S.Header>
                <S.HeaderIcon path={mdiCashMultiple} size={0.9} />
                <S.HeaderTitle>Record payment</S.HeaderTitle>
            </S.Header>

            <S.FormSection>
                <S.Label>Payment Date</S.Label>
                <DatePicker
                    value={paymentDate}
                    onDateSelected={handleDateChange}
                    errorMessage={errors.paymentDate}
                />
            </S.FormSection>

            <S.FormSection>
                <S.Label>Amount</S.Label>
                <S.AmountField>
                    <S.AmountPrefix>$</S.AmountPrefix>
                    <BaseInput
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                        disableLatin1Validation
                    />
                </S.AmountField>
                {errors.amount && <S.ErrorText>{errors.amount}</S.ErrorText>}
            </S.FormSection>

            <StatementApplyToClaims
                applyMode="manual"
                openClaims={payableClaims}
                selectableClaims={payableClaims}
                selectedClaimIds={selectedClaimIds}
                onToggleClaim={selectClaim}
                targetClaims={targetClaims}
                errorMessage={errors.claims}
                hideBalance
                selectionOnly
            />

            <S.FormSection>
                <S.Label>Notes</S.Label>
                <BaseInput
                    value={memo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemo(e.target.value)}
                    placeholder="Enter memo"
                />
            </S.FormSection>

            <S.ButtonGroup
                changed={true}
                useNotify={false}
                onCancel={onClose}
                onSave={handleSubmit}
                saveLabel={isSubmitting ? 'Recording...' : 'Record payment'}
                saveNotice="Payment recorded successfully"
            />
        </S.DialogBody>
    );
};
